// src/api/client.ts
// Centralized HTTP client — all API calls go through here

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { appConfig } from '../config/app.config';
import { ApiErrorResponse, ApiResponse } from './types';

// ── Retry configuration ──────────────────────────────────────────────────────
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 800;
const RETRYABLE_METHODS = ['GET', 'HEAD'];
const RETRYABLE_STATUSES = [502, 503, 504];

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ── Normalized API Error ─────────────────────────────────────────────────────
export class ApiError extends Error {
  public statusCode: number;
  public errors?: Record<string, string[]>;
  public originalError: AxiosError;

  constructor(message: string, statusCode: number, originalError: AxiosError, errors?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.errors = errors;
    this.originalError = originalError;
  }

  get isUnauthorized() {
    return this.statusCode === 401;
  }
  get isForbidden() {
    return this.statusCode === 403;
  }
  get isNotFound() {
    return this.statusCode === 404;
  }
  get isServerError() {
    return this.statusCode >= 500;
  }
}

// ── Create Axios Instance ────────────────────────────────────────────────────
function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: appConfig.apiBaseUrl,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  // ── Request interceptor: attach JWT ───────────────────────────────────────
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = getStoredToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // ── Response interceptor: normalize + retry ───────────────────────────────
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const config = error.config as AxiosRequestConfig & { _retryCount?: number };

      // Handle retries for transient server errors
      if (
        config &&
        RETRYABLE_METHODS.includes((config.method || '').toUpperCase()) &&
        RETRYABLE_STATUSES.includes(error.response?.status || 0)
      ) {
        config._retryCount = (config._retryCount || 0) + 1;
        if (config._retryCount <= MAX_RETRIES) {
          await delay(RETRY_DELAY_MS * config._retryCount);
          return instance(config);
        }
      }

      // Normalize to ApiError
      if (axios.isAxiosError(error)) {
        const status = error.response?.status || 0;
        const body = error.response?.data as ApiErrorResponse | undefined;
        const message = body?.message || error.message || 'An unexpected error occurred';
        const errors = body?.errors;

        // Trigger global 401 event so auth store can clear session
        if (status === 401) {
          window.dispatchEvent(new Event('hams:unauthorized'));
        }

        return Promise.reject(new ApiError(message, status, error, errors));
      }

      return Promise.reject(error);
    }
  );

  return instance;
}

// ── Token helpers (storage-agnostic) ─────────────────────────────────────────
export function getStoredToken(): string | null {
  return sessionStorage.getItem('hams_token') || localStorage.getItem('hams_token');
}

export function storeToken(token: string, remember = false): void {
  if (remember) {
    localStorage.setItem('hams_token', token);
  } else {
    sessionStorage.setItem('hams_token', token);
  }
}

export function clearStoredToken(): void {
  sessionStorage.removeItem('hams_token');
  localStorage.removeItem('hams_token');
}

// ── Exported client singleton ─────────────────────────────────────────────────
export const apiClient = createApiClient();

// ── Convenience wrapper that unwraps ApiResponse<T> ──────────────────────────
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const { data } = await apiClient.get<ApiResponse<T>>(url, { params });
  return data.data as T;
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.post<ApiResponse<T>>(url, body);
  return data.data as T;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const { data } = await apiClient.put<ApiResponse<T>>(url, body ?? {});
  return data.data as T;
}

export async function apiDelete<T>(url: string): Promise<T> {
  const { data } = await apiClient.delete<ApiResponse<T>>(url);
  return data.data as T;
}
